import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

/**
 * 24-hour availability for one location from `location_uptime_samples`
 * (snapshots of online/total device counts written from UniFi). The figure
 * is the mean of the samples in the window; "—" when there are none.
 */
export interface UptimeSample {
  sampled_at: string;
  online_count: number;
  total_count: number;
  online_pct: number;
}

export interface LocationUptime {
  samples: UptimeSample[];
  availabilityPct: number | null;
  latestSampledAt: string | null;
  latestOnline: number | null;
  latestTotal: number | null;
}

/** snapshot_unifi_uptime() samples every 5 minutes. */
export const UPTIME_EXPECTED_REFRESH_MS = 15 * 60 * 1000;
export const UPTIME_STALE_MS = 60 * 60 * 1000;

export interface DailyUptimePoint {
  /** yyyy-mm-dd */
  day: string;
  /** Mean online percentage across that day's samples. */
  pct: number;
  /** Lowest sample seen that day — an outage a mean would hide. */
  worstPct: number;
  sampleCount: number;
}

/**
 * Daily availability for the trend line, aggregated in the database.
 *
 * Production writes a sample every five minutes per location, so a month is
 * several thousand rows. Fetching them to bucket in the browser meant any row
 * cap silently shortened the window — a "30-day" line quietly showing a week.
 * location_uptime_daily() does the grouping server-side and checks the caller
 * may see the location.
 */
export function useLocationUptimeDaily(locationId: string | undefined, days = 30) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['location-uptime-daily', scope.key, locationId ?? '', days],
    enabled: Boolean(locationId) && scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<DailyUptimePoint[]> => {
      const { data, error } = await supabase.rpc('location_uptime_daily', {
        p_location_id: locationId!,
        p_days: days,
      });
      if (error) throw new Error(error.message);
      type Row = { day: string; mean_online_pct: number; worst_online_pct: number; sample_count: number };
      return ((data ?? []) as unknown as Row[]).map((row) => ({
        day: row.day,
        pct: Number(row.mean_online_pct),
        worstPct: Number(row.worst_online_pct),
        sampleCount: Number(row.sample_count),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useLocationUptime(locationId: string | undefined, hours = 24) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['location-uptime', scope.key, locationId ?? '', hours],
    enabled: Boolean(locationId) && scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<LocationUptime> => {
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      // This read backs the headline availability figure over a short window.
      // The 30-day trend does not come through here — it is aggregated by
      // location_uptime_daily() — so 500 rows is a whole day at the production
      // five-minute cadence with room to spare.
      const rowCap = 500;
      const { data, error } = await supabase
        .from('location_uptime_samples')
        .select('sampled_at, online_count, total_count, online_pct')
        .eq('location_id', locationId!)
        .in('customer_id', scope.ids)
        .gte('sampled_at', since)
        .order('sampled_at', { ascending: false })
        .limit(rowCap);
      if (error) throw error;
      const samples = (data ?? []).map((s) => ({
        sampled_at: s.sampled_at,
        online_count: s.online_count,
        total_count: s.total_count,
        online_pct: Number(s.online_pct),
      }));
      const valid = samples.filter((s) => Number.isFinite(s.online_pct));
      const availabilityPct =
        valid.length > 0
          ? valid.reduce((sum, s) => sum + s.online_pct, 0) / valid.length
          : null;
      return {
        samples,
        availabilityPct,
        latestSampledAt: samples[0]?.sampled_at ?? null,
        latestOnline: samples[0]?.online_count ?? null,
        latestTotal: samples[0]?.total_count ?? null,
      };
    },
    staleTime: 60 * 1000,
  });
}
